import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ChevronDown, MessageCircle, ShieldCheck } from 'lucide-react';
import { CONTACT_INFO } from '../../data/content';
import { scrollToSection } from '../../lib/scroll';

const HERO_BG =
  'https://images.unsplash.com/photo-1764239651625-c611fe706915?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxmaXJlJTIwc2FmZXR5JTIwZGFyayUyMGJhY2tncm91bmQlMjBzcGFya3MlMjBpbmR1c3RyaWFsJTIwc21va2V8ZW58MHx8fHwxNzkyNjg5MjIzfDA&ixlib=rb-4.1.0&q=85';
const HERO_PRODUCT = '/hero-collage.webp';

const lineVariants = {
  hidden: { y: '115%' },
  visible: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.1 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskedLine = ({ index, children, className = '' }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      custom={index}
      variants={lineVariants}
      initial="hidden"
      animate="visible"
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const productY = useTransform(scrollYProgress, [0, 1], ['0%', '-18%']);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-[#040D1A]"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img src={HERO_BG} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-20" />
      </motion.div>
      <div className="absolute inset-0 vignette" />
      <div className="absolute -top-40 right-[-12%] w-[46rem] h-[46rem] spotlight-blue opacity-80 pointer-events-none" />
      <div className="absolute bottom-[-25%] left-[-12%] w-[42rem] h-[42rem] spotlight-yellow opacity-70 pointer-events-none" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 container mx-auto px-4 pt-36 pb-24 grid lg:grid-cols-12 gap-12 items-center"
      >
        <div className="lg:col-span-7">
          <MaskedLine index={0}>
            <span
              data-testid="hero-badge"
              className="inline-flex items-center gap-3 font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-amber-300 border border-amber-400/30 rounded-full px-4 py-2 bg-amber-400/5"
            >
              <ShieldCheck className="w-4 h-4" />
              Desde 2017 · +7 anos protegendo vidas
            </span>
          </MaskedLine>

          <h1
            data-testid="hero-headline"
            className="mt-8 font-display font-extrabold uppercase tracking-tight leading-[0.95] text-[clamp(2.6rem,7vw,5.5rem)]"
          >
            <MaskedLine index={1}>Proteção contra</MaskedLine>
            <MaskedLine index={2} className="text-amber-400 italic">
              fogo de verdade
            </MaskedLine>
            <MaskedLine index={3}>
              <span className="text-outline-yellow">sem margem</span> para erro.
            </MaskedLine>
          </h1>

          <MaskedLine index={4}>
            <p
              data-testid="hero-subtitle"
              className="mt-8 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
            >
              Equipamentos certificados, projetos técnicos e treinamento de brigadas.
              A Leão de Fogo protege o que realmente importa — sua empresa e suas pessoas.
            </p>
          </MaskedLine>

          <MaskedLine index={5}>
            <div className="mt-10 flex flex-wrap gap-4">
              <button
                data-testid="hero-cta-produtos"
                onClick={() => scrollToSection('#produtos')}
                className="group inline-flex items-center gap-2 bg-amber-400 text-[#0A2847] font-display font-bold uppercase tracking-wide text-sm px-8 py-4 rounded-full hover:bg-amber-300 hover:shadow-[0_0_32px_rgba(252,211,77,0.45)] transition-all duration-300"
              >
                Ver Produtos
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <a
                data-testid="hero-cta-whatsapp"
                href={`https://wa.me/${CONTACT_INFO.whatsappNumber}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-amber-400/40 text-amber-300 font-display font-bold uppercase tracking-wide text-sm px-8 py-4 rounded-full hover:bg-amber-400/10 hover:border-amber-300 transition-all duration-300"
              >
                <MessageCircle className="w-4 h-4" />
                Falar com Especialista
              </a>
            </div>
          </MaskedLine>
        </div>

        <motion.div style={{ y: productY }} className="relative hidden lg:block lg:col-span-5">
          <div className="absolute inset-0 spotlight-yellow scale-125 pointer-events-none" />
          <motion.div
            initial={{ opacity: 0, y: 60, rotate: 4 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ duration: 1.1, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="animate-float-slow rounded-3xl overflow-hidden border border-amber-400/20 shadow-[0_40px_80px_rgba(0,0,0,0.6)]">
              <img
                data-testid="hero-product-image"
                src={HERO_PRODUCT}
                alt="Leão de Fogo em ação — treinamentos e equipamentos"
                className="w-full max-h-[470px] xl:max-h-[540px] object-contain"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.35, duration: 0.7 }}
              className="absolute top-6 -left-4 bg-[#0A2847]/85 backdrop-blur-md border border-amber-400/25 rounded-xl px-5 py-4 flex items-center gap-3"
            >
              <ShieldCheck className="w-8 h-8 text-amber-400 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-white">Produtos certificados</p>
                <p className="text-xs text-slate-400">Conforme normas do Corpo de Bombeiros</p>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToSection('#produtos')}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-amber-300/70 hover:text-amber-300 transition-colors"
        aria-label="Rolar para produtos"
      >
        <ChevronDown className="w-7 h-7 animate-bounce" />
      </motion.button>
    </section>
  );
};

export default Hero;
